/**
 * Custom header behaviour (Hofman Animal Care).
 *
 * Enhances the "Categorieën" dropdown (a native <details> disclosure) so it
 * closes when clicking outside of it or pressing Escape. Nested flyouts (3rd
 * menu level) are reparented to the panel and positioned like column 2
 * (top: 0, full column height) so they align with the root menu and are not
 * clipped by scrollable parent columns. Works without JS via CSS fallback.
 */

const SELECTOR = 'details[data-custom-categories]';
const DETACHED_CLASS = 'custom-categories__menu--sub--detached';
const VISIBLE_CLASS = 'custom-categories__menu--sub--visible';
const ACTIVE_BRANCH_CLASS = 'custom-categories__item--branch-active';

/** @type {WeakMap<HTMLElement, HTMLElement>} */
const flyoutOwners = new WeakMap();

/** @type {WeakMap<HTMLElement, () => void>} */
const nestedFlyoutResetters = new WeakMap();

/**
 * Close a categories dropdown.
 * @param {HTMLDetailsElement} details
 */
function closeDropdown(details) {
  if (details.open) {
    details.open = false;
    resetNestedFlyouts(details);
  }
}

/**
 * Close all open categories dropdowns except an optional one to keep open.
 * @param {HTMLDetailsElement | null} [except]
 */
function closeAll(except) {
  document.querySelectorAll(SELECTOR).forEach((details) => {
    if (details !== except && details instanceof HTMLDetailsElement) {
      closeDropdown(details);
    }
  });
}

/**
 * @param {HTMLElement} flyout
 */
function resetNestedFlyout(flyout) {
  // Remove visible state BEFORE re-parenting: while the flyout has
  // display:block and an absolute left >300px it would briefly overflow the
  // scrollable level-2 column horizontally, causing a scrollbar to flash.
  flyout.classList.remove(DETACHED_CLASS, VISIBLE_CLASS);
  flyout.style.removeProperty('top');
  flyout.style.removeProperty('left');
  flyout.style.removeProperty('bottom');
  flyout.style.removeProperty('height');
  flyout.style.removeProperty('width');

  const owner = flyoutOwners.get(flyout);
  if (owner instanceof HTMLElement && flyout.parentElement !== owner) {
    owner.appendChild(flyout);
  }
}

/**
 * @param {HTMLElement} scope
 */
function clearActiveBranch(scope) {
  scope.querySelectorAll(`.${ACTIVE_BRANCH_CLASS}`).forEach((item) => {
    item.classList.remove(ACTIVE_BRANCH_CLASS);
  });
}

/**
 * Houd kolom 1 + 2 open terwijl kolom 3 (losgekoppeld) gehoverd wordt.
 * @param {HTMLElement} item
 */
function setActiveBranch(item) {
  const categories = item.closest('.custom-categories');

  if (!(categories instanceof HTMLElement)) {
    return;
  }

  clearActiveBranch(categories);

  let current = item;

  while (current instanceof HTMLElement) {
    if (current.classList.contains('custom-categories__item')) {
      current.classList.add(ACTIVE_BRANCH_CLASS);
    }

    const parentItem = current.parentElement?.closest('.custom-categories__item');
    current = parentItem instanceof HTMLElement ? parentItem : null;
  }
}

/**
 * @param {ParentNode} root
 */
function resetNestedFlyouts(root) {
  const scopes =
    root instanceof HTMLElement && root.classList.contains('custom-categories')
      ? [root]
      : [...root.querySelectorAll('.custom-categories')];

  for (const scope of scopes) {
    if (!(scope instanceof HTMLElement)) {
      continue;
    }

    scope.querySelectorAll(`.${DETACHED_CLASS}`).forEach((flyout) => {
      if (flyout instanceof HTMLElement) {
        resetNestedFlyout(flyout);
      }
    });

    clearActiveBranch(scope);
  }
}

/**
 * @param {HTMLElement} item
 * @returns {boolean}
 */
function isNestedFlyoutItem(item) {
  const flyout = item.querySelector(':scope > .custom-categories__menu--sub');
  const parentColumn = item.parentElement?.closest('.custom-categories__menu--sub');

  return Boolean(flyout && parentColumn);
}

/**
 * @param {HTMLElement} flyout
 * @param {HTMLElement} panel
 * @param {HTMLElement} parentColumn
 */
function layoutDetachedFlyout(flyout, panel, parentColumn) {
  /* getBoundingClientRect geeft de gerenderde positie, onafhankelijk van
     transforms en subpixel-afronding. left = rechterrand van kolom 2 minus
     linkerrand van het panel (beide in viewport-coördinaten). */
  const panelRect = panel.getBoundingClientRect();
  const colRect = parentColumn.getBoundingClientRect();
  flyout.style.top = '0px';
  flyout.style.bottom = '0px';
  flyout.style.left = `${Math.round(colRect.right - panelRect.left)}px`;
  flyout.style.height = '';
  flyout.style.width = '';
}

/**
 * Reparent a 3rd-level flyout to the panel so it aligns with column 2 (top: 0)
 * and is not clipped by the scrollable parent column.
 * @param {HTMLElement} item
 */
function showNestedFlyout(item) {
  const flyout = item.querySelector(':scope > .custom-categories__menu--sub');
  const panel = item.closest('.custom-categories__panel');
  const parentColumn = item.parentElement?.closest('.custom-categories__menu--sub');
  const categories = item.closest('.custom-categories');

  if (!(flyout instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(parentColumn instanceof HTMLElement)) {
    return;
  }

  if (categories instanceof HTMLElement) {
    for (const detachedFlyout of categories.querySelectorAll(`.${DETACHED_CLASS}`)) {
      if (detachedFlyout !== flyout && detachedFlyout instanceof HTMLElement) {
        resetNestedFlyout(detachedFlyout);
      }
    }
  }

  flyoutOwners.set(flyout, item);
  setActiveBranch(item);

  if (flyout.parentElement !== panel) {
    panel.appendChild(flyout);
  }

  flyout.classList.add(DETACHED_CLASS, VISIBLE_CLASS);
  layoutDetachedFlyout(flyout, panel, parentColumn);
}

/**
 * @param {HTMLElement} categories
 */
function bindNestedFlyoutPositioning(categories) {
  if (nestedFlyoutResetters.has(categories)) {
    return;
  }

  /** @type {HTMLElement | null} */
  let activeItem = null;

  const repositionActiveFlyout = () => {
    if (!activeItem) {
      return;
    }

    const panel = activeItem.closest('.custom-categories__panel');
    const parentColumn = activeItem.parentElement?.closest('.custom-categories__menu--sub');

    // When detached the flyout is no longer a direct child of activeItem;
    // find it via the owner map instead.
    let flyout = activeItem.querySelector(':scope > .custom-categories__menu--sub');
    if (!flyout && panel instanceof HTMLElement) {
      for (const detached of panel.querySelectorAll(`.${DETACHED_CLASS}`)) {
        if (detached instanceof HTMLElement && flyoutOwners.get(detached) === activeItem) {
          flyout = detached;
          break;
        }
      }
    }

    if (
      flyout instanceof HTMLElement &&
      panel instanceof HTMLElement &&
      parentColumn instanceof HTMLElement &&
      flyout.classList.contains(DETACHED_CLASS)
    ) {
      layoutDetachedFlyout(flyout, panel, parentColumn);
    }
  };

  const handlePointerOver = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    // Pointer is inside the detached (level-3) flyout itself — keep it alive.
    if (event.target.closest(`.${DETACHED_CLASS}`)) {
      if (activeItem instanceof HTMLElement) {
        setActiveBranch(activeItem);
      }
      return;
    }

    const item = event.target.closest('.custom-categories__item--has-children');

    // Re-entering the same level-2 item that owns the currently-open flyout.
    // isNestedFlyoutItem() would return false here because the flyout is
    // detached (no longer a direct child), so guard against that explicitly.
    if (item instanceof HTMLElement && item === activeItem) {
      setActiveBranch(item);
      return;
    }

    // Pointer is in the top/bottom padding of a sub-column (the <ul> gap area,
    // not over any <li>) — keep the active flyout open during transitions.
    const inSubColumn = event.target.closest('.custom-categories__menu--sub');
    const inAnyItem = event.target.closest('.custom-categories__item');
    if (inSubColumn && !inAnyItem) {
      return;
    }

    if (!(item instanceof HTMLElement) || !categories.contains(item) || !isNestedFlyoutItem(item)) {
      resetNestedFlyouts(categories);
      activeItem = null;
      return;
    }

    activeItem = item;
    showNestedFlyout(item);
  };

  const handleCategoriesPointerLeave = (event) => {
    const relatedTarget = event.relatedTarget;

    if (relatedTarget instanceof Node && categories.contains(relatedTarget)) {
      return;
    }

    resetNestedFlyouts(categories);
    activeItem = null;
  };

  const handleFocusIn = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const item = event.target.closest('.custom-categories__item--has-children');

    if (!(item instanceof HTMLElement) || !categories.contains(item) || !isNestedFlyoutItem(item)) {
      return;
    }

    activeItem = item;
    showNestedFlyout(item);
  };

  categories.addEventListener('pointerover', handlePointerOver);
  categories.addEventListener('pointerleave', handleCategoriesPointerLeave);
  categories.addEventListener('focusin', handleFocusIn);

  for (const column of categories.querySelectorAll('.custom-categories__menu--sub')) {
    column.addEventListener('scroll', repositionActiveFlyout, { passive: true });
  }

  window.addEventListener('resize', repositionActiveFlyout, { passive: true });
  window.addEventListener('scroll', repositionActiveFlyout, { passive: true });

  nestedFlyoutResetters.set(categories, () => {
    categories.removeEventListener('pointerover', handlePointerOver);
    categories.removeEventListener('pointerleave', handleCategoriesPointerLeave);
    categories.removeEventListener('focusin', handleFocusIn);
    window.removeEventListener('resize', repositionActiveFlyout);
    window.removeEventListener('scroll', repositionActiveFlyout);

    for (const column of categories.querySelectorAll('.custom-categories__menu--sub')) {
      column.removeEventListener('scroll', repositionActiveFlyout);
    }

    resetNestedFlyouts(categories);
    activeItem = null;
  });
}

/**
 * @param {ParentNode} [root=document]
 */
function initCategoryNestedFlyouts(root = document) {
  for (const categories of root.querySelectorAll('.custom-categories')) {
    if (categories instanceof HTMLElement) {
      bindNestedFlyoutPositioning(categories);
    }
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const openDropdown = target.closest(SELECTOR);

  // Clicked outside any dropdown: close them all.
  if (!openDropdown) {
    closeAll(null);
    return;
  }

  // Clicked inside one dropdown: close the others.
  if (openDropdown instanceof HTMLDetailsElement) {
    closeAll(openDropdown);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const openDropdowns = document.querySelectorAll(`${SELECTOR}[open]`);
  if (openDropdowns.length === 0) return;

  openDropdowns.forEach((details) => {
    if (details instanceof HTMLDetailsElement) {
      closeDropdown(details);
      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus();
    }
  });
});

initCategoryNestedFlyouts();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initCategoryNestedFlyouts(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  for (const categories of event.target.querySelectorAll('.custom-categories')) {
    if (!(categories instanceof HTMLElement)) continue;
    const resetter = nestedFlyoutResetters.get(categories);
    if (resetter) {
      resetter();
      nestedFlyoutResetters.delete(categories);
    }
  }
});
